import { AxiosResponse, AxiosError } from 'axios';

/**
 * Valida se a resposta da API é bem-sucedida (status 200 ou 201)
 * Se não for, lança uma exceção com mensagem apropriada
 * 
 * @param response - Resposta da API do Axios
 * @throws {Error} - Lança erro se o status não for 200 ou 201
 */
export const validateApiResponse = (response: AxiosResponse): void => {
  const status = response?.status;
  
  if (status === 200 || status === 201) {
    return;
  }
  
  const errorMessage = getErrorMessage(status, response?.data);
  throw new Error(errorMessage);
};

/**
 * Valida resposta de API e retorna a resposta se for válida
 * Útil para usar em try/catch
 * 
 * @param response - Resposta da API do Axios
 * @returns A resposta se for válida
 * @throws {Error} - Lança erro se o status não for 200 ou 201
 */
export const validateAndReturnResponse = <T = any>(response: AxiosResponse<T>): AxiosResponse<T> => {
  validateApiResponse(response);
  return response;
};

/**
 * Traduz mensagens de erro comuns do backend de inglês para português
 * 
 * @param message - Mensagem de erro em inglês
 * @returns Mensagem traduzida para português
 */
const translateErrorMessage = (message: string): string => {
  const translations: Record<string, string> = {
    'Files of type': 'Arquivos do tipo',
    'are not supported': 'não são suportados',
    'not supported': 'não suportado',
    'File type': 'Tipo de arquivo',
    'is not allowed': 'não é permitido',
    'is not permitted': 'não é permitido',
    'Invalid file': 'Arquivo inválido',
    'File too large': 'Arquivo muito grande',
    'File size': 'Tamanho do arquivo',
    'exceeds': 'excede',
    'maximum': 'máximo',
    'allowed': 'permitido',
  };

  let translatedMessage = message;

  if (message.includes('Files of type') && message.includes('are not supported')) {
    const mimeTypeMatch = message.match(/Files of type ([^ ]+) are not supported/i);
    if (mimeTypeMatch) {
      const mimeType = mimeTypeMatch[1];
      const fileTypeNames: Record<string, string> = {
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'Word (.docx)',
        'application/msword': 'Word (.doc)',
        'application/vnd.ms-excel': 'Excel (.xls)',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'Excel (.xlsx)',
        'application/vnd.ms-powerpoint': 'PowerPoint (.ppt)',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'PowerPoint (.pptx)',
        'application/pdf': 'PDF',
        'image/jpeg': 'JPEG',
        'image/png': 'PNG',
        'image/gif': 'GIF',
      };
      
      const fileTypeName = fileTypeNames[mimeType] || mimeType;
      translatedMessage = `Arquivos do tipo ${fileTypeName} não são suportados. Verifique os tipos permitidos.`;
    } else {
      translatedMessage = message.replace(/Files of type/i, 'Arquivos do tipo')
                                  .replace(/are not supported/i, 'não são suportados');
    }
  }

  Object.keys(translations).forEach(key => {
    const regex = new RegExp(key, 'gi');
    if (regex.test(translatedMessage) && !translatedMessage.includes('Files of type')) {
      translatedMessage = translatedMessage.replace(regex, translations[key]);
    }
  });

  return translatedMessage;
};

/**
 * Processa erros de arquivos da estrutura erros_arquivos do backend
 * 
 * @param errosArquivos - Objeto com erros de arquivos por campo
 * @returns Mensagem formatada com todos os erros de arquivos
 */
const processFileErrors = (errosArquivos: any): string => {
  if (!errosArquivos || typeof errosArquivos !== 'object') {
    return '';
  }

  const errorMessages: string[] = [];

  Object.keys(errosArquivos).forEach(campoId => {
    const erros = errosArquivos[campoId];
    
    if (Array.isArray(erros)) {
      erros.forEach((erro: any) => {
        if (erro && typeof erro === 'object') {
          const nomeArquivo = erro.arquivo || 'arquivo desconhecido';
          const mensagemErro = erro.erro || erro.error || 'Erro desconhecido';
          const nomeCampo = erro.campo || `Campo ${campoId}`;
          
          errorMessages.push(`${nomeCampo} - "${nomeArquivo}": ${mensagemErro}`);
        }
      });
    }
  });

  if (errorMessages.length > 0) {
    return errorMessages.join('\n');
  }

  return '';
};

/**
 * Extrai mensagem de erro apropriada baseada no status HTTP
 * 
 * @param status - Status HTTP da resposta
 * @param data - Dados da resposta (pode conter mensagem de erro)
 * @param isFileUpload - Indica se é um erro de upload de arquivo
 * @returns Mensagem de erro formatada
 */
const getErrorMessage = (status: number, data?: any, isFileUpload = false): string => {
  if (data?.erros_arquivos && typeof data.erros_arquivos === 'object') {
    const fileErrorsMessage = processFileErrors(data.erros_arquivos);
    if (fileErrorsMessage) {
      return fileErrorsMessage;
    }
  }

  let rawMessage: string | undefined;
  
  if (data?.message) {
    rawMessage = data.message;
  } else if (data?.error) {
    rawMessage = data.error;
  }
  
  if (rawMessage && isFileUpload) {
    return translateErrorMessage(rawMessage);
  }
  
  if (rawMessage) {
    return rawMessage;
  }
  
  if (Array.isArray(data) && data.length > 0) {
    const firstError = data[0];
    if (typeof firstError === 'string') {
      return isFileUpload ? translateErrorMessage(firstError) : firstError;
    }
    if (typeof firstError === 'object') {
      if (firstError.message) {
        return isFileUpload ? translateErrorMessage(firstError.message) : firstError.message;
      }
      if (firstError.error) {
        return isFileUpload ? translateErrorMessage(firstError.error) : firstError.error;
      }
      const errorKeys = Object.keys(firstError);
      if (errorKeys.length > 0) {
        const firstKey = errorKeys[0];
      const errorValue = firstError[firstKey];
      if (Array.isArray(errorValue) && errorValue.length > 0) {
        const errorMsg = typeof errorValue[0] === 'string' ? errorValue[0] : String(errorValue[0]);
        return `${firstKey}: ${isFileUpload ? translateErrorMessage(errorMsg) : errorMsg}`;
      }
      const errorMsg = typeof errorValue === 'string' ? errorValue : String(errorValue);
      return `${firstKey}: ${isFileUpload ? translateErrorMessage(errorMsg) : errorMsg}`;
      }
    }
    return JSON.stringify(firstError);
  }
  
  if (typeof data === 'object' && data !== null) {
    const keys = Object.keys(data);
    if (keys.length > 0) {
      const firstKey = keys[0];
      const errorValue = data[firstKey];
      if (Array.isArray(errorValue) && errorValue.length > 0) {
        const errorMsg = typeof errorValue[0] === 'string' ? errorValue[0] : String(errorValue[0]);
        return `${firstKey}: ${isFileUpload ? translateErrorMessage(errorMsg) : errorMsg}`;
      }
      if (typeof errorValue === 'string') {
        return `${firstKey}: ${isFileUpload ? translateErrorMessage(errorValue) : errorValue}`;
      }
    }
  }
  
  const statusMessages: Record<number, string> = {
    400: isFileUpload 
      ? 'Tipo de arquivo não permitido ou arquivo inválido. Verifique o formato e tente novamente.'
      : 'Requisição inválida. Verifique os dados enviados.',
    401: 'Não autorizado. Faça login novamente.',
    403: 'Acesso negado. Você não tem permissão para esta ação.',
    404: 'Recurso não encontrado.',
    406: isFileUpload
      ? 'Formato de arquivo não aceito. Verifique os tipos permitidos.'
      : 'Formato não aceito.',
    413: isFileUpload
      ? 'Arquivo muito grande. Reduza o tamanho do arquivo e tente novamente.'
      : 'Arquivo muito grande.',
    415: isFileUpload
      ? 'Tipo de mídia não suportado. Verifique o formato do arquivo.'
      : 'Tipo de mídia não suportado.',
    500: 'Erro interno do servidor. Tente novamente mais tarde.',
    501: 'Funcionalidade não implementada no servidor.',
    502: 'Erro de gateway. Tente novamente mais tarde.',
    503: 'Serviço temporariamente indisponível. Tente novamente mais tarde.',
    504: 'Tempo de resposta excedido. Tente novamente.',
  };
  
  return statusMessages[status] || `Erro na requisição (Status: ${status}). Tente novamente.`;
};

/**
 * Trata erros do Axios e retorna mensagem de erro formatada
 * 
 * @param error - Erro do Axios
 * @param isFileUpload - Indica se é um erro de upload de arquivo
 * @returns Mensagem de erro formatada
 */
export const handleApiError = (error: AxiosError | Error, isFileUpload = false): string => {
  if ('response' in error && error.response) {
    const status = error.response.status;
    const data = error.response.data;
    return getErrorMessage(status, data, isFileUpload);
  }
  
  if ('message' in error) {
    return error.message || 'Erro desconhecido. Tente novamente.';
  }
  
  return isFileUpload
    ? 'Erro ao enviar arquivo. Verifique sua conexão e tente novamente.'
    : 'Erro na comunicação com o servidor. Verifique sua conexão e tente novamente.';
};
